import React from "react";
import { Box, Typography } from "@mui/material";
import { ColDef } from "ag-grid-community";
import { Deployment } from "../../../redux/deployments/deployments-types";
import { formatDateTime } from "../../../helpers/time";
import { formatDevice } from "../../../helpers/device";
import { formatLatLon } from "../../../helpers/common";
import { latestPosition } from "../../deployments/utils";
import { MinimalAgTable } from "./MinimalTableAG";

type DeploymentPanelProps = {
  deployments: Deployment[];
};

const timeColumnWidth = 100;
const latlonColumnWidth = 100;
const measureFieldWidth = 70;

const deploymentColumnDefs: ColDef[] = [
  { field: "station", headerName: "Station", width: 100 },
  { field: "start", headerName: "Start", width: timeColumnWidth },
  { field: "end", headerName: "End", width: timeColumnWidth },
  { field: "latLon", headerName: "Latest Lat/Lon", width: latlonColumnWidth },
];

const deviceColumnDefs: ColDef[] = [
  { field: "device", headerName: "Device", width: 140 },
  { field: "height", headerName: "Height", width: measureFieldWidth },
  { field: "start", headerName: "Start", width: timeColumnWidth },
  { field: "end", headerName: "End", width: timeColumnWidth },
];

const positionColumnDefs: ColDef[] = [
  { field: "latLon", headerName: "Lat/Lon", width: latlonColumnWidth },
  { field: "depth", headerName: "Depth", width: measureFieldWidth },
  { field: "start", headerName: "Start", width: timeColumnWidth },
  { field: "end", headerName: "End", width: timeColumnWidth },
];

export const DeploymentPanel: React.FC<DeploymentPanelProps> = ({ deployments }) => {
  if (!deployments || deployments.length === 0) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          No deployment data available
        </Typography>
      </Box>
    );
  }

  const deploymentRows = deployments.map(d => {
    const latest = latestPosition(d);
    return {
      id: d.id,
      station: d.station?.name || "",
      start: formatDateTime(d.start),
      end: formatDateTime(d.end),
      latLon: latest?.latLon
        ? `(${formatLatLon(latest.latLon.latitude)}, ${formatLatLon(latest.latLon.longitude)})`
        : "",
    };
  });

  // flatten all attachments from all deployments
  const deviceRows = deployments.flatMap(d =>
    (d.deviceAttachments || []).map(da => ({
      id: da.id,
      device: da.device ? formatDevice(da.device) : "",
      height: da.height ? `${da.height.value} ${da.height.unit}` : "",
      start: formatDateTime(da.start),
      end: formatDateTime(da.end),
    }))
  );

  // flatten all positions from all deployments
  const positionRows = deployments.flatMap(d => {
    return d.positions.map(p => ({
      id: p.id,
      latLon: p.latLon
        ? `(${formatLatLon(p.latLon.latitude)}, ${formatLatLon(p.latLon.longitude)})`
        : "",
      depth: p.depth ? `${p.depth.value} ${p.depth.unit}` : "",
      start: formatDateTime(p.start),
      end: formatDateTime(p.end),
    }));
  });

  return (
    <Box sx={{ overflow: "auto", height: "100%", width: "100%" }}>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 460 }}>
        <Box p={2}>
          <Typography variant="h6" gutterBottom>
            Deployment Details
          </Typography>
        </Box>

        <Box px={2} pb={1}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Deployment
          </Typography>
          <MinimalAgTable rowData={deploymentRows} columnDefs={deploymentColumnDefs} />
        </Box>

        {deviceRows.length > 0 && (
          <Box px={2} py={1}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Device Attachments
            </Typography>
            <MinimalAgTable rowData={deviceRows} columnDefs={deviceColumnDefs} />
          </Box>
        )}

        {positionRows.length > 0 && (
          <Box px={2} py={1}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Deployment Positions
            </Typography>
            <MinimalAgTable rowData={positionRows} columnDefs={positionColumnDefs} />
          </Box>
        )}
      </Box>
    </Box>
  );
};
